import { FeaturedAlbumBanner } from "@/components/ui/FeaturedAlbumBanner";
import { DetailAlbumDto } from "@/lib/lutely-api/gen/dtos";
import { cn } from "@/lib/utils";
import { Box, Flex } from "@radix-ui/themes";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";

type FeaturedCarouselItem = {
  album: DetailAlbumDto;
  isExclusive?: boolean;
  creatorIdOverride?: string;
  stageNameOverride?: string;
  labelOverride?: string;
  bannerImageUrl?: string;
  imagePosition?: string;
  animateImage?: boolean;
};

type FeaturedAlbumCarouselProps = {
  items: FeaturedCarouselItem[];
  className?: string;
};

const AUTOPLAY_DELAY_MS = 3000;

export function FeaturedAlbumCarousel({
  items,
  className,
}: FeaturedAlbumCarouselProps) {
  const count = items.length;
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "start", duration: 30 },
    count > 1
      ? [
          Autoplay({
            delay: AUTOPLAY_DELAY_MS,
            stopOnInteraction: false,
            stopOnMouseEnter: true,
          }),
        ]
      : [],
  );
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setActiveIndex(emblaApi.selectedScrollSnap());
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi]);

  const scrollTo = useCallback(
    (idx: number) => {
      emblaApi?.scrollTo(idx);
      emblaApi?.plugins().autoplay?.reset();
    },
    [emblaApi],
  );

  return (
    <Box
      className={cn("relative w-full", className)}
      aria-roledescription="carousel"
    >
      <Box
        ref={emblaRef}
        className="relative w-full overflow-hidden rounded-lg"
      >
        <Flex className="w-full">
          {items.map((config, index) => (
            <Box
              key={config.album.albumId}
              className="min-w-0 flex-[0_0_100%]"
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${count}`}
            >
              <FeaturedAlbumBanner
                album={config.album}
                isExclusive={config.isExclusive}
                stageNameOverride={config.stageNameOverride}
                labelOverride={config.labelOverride}
                bannerImageUrl={config.bannerImageUrl}
                imagePosition={config.imagePosition}
                animateImage={config.animateImage}
              />
            </Box>
          ))}
        </Flex>
      </Box>

      {count > 1 && (
        <Box className="absolute bottom-3 left-1/2 z-20 -translate-x-1/2">
          <Flex align="center" gap="2">
            {items.map((_, idx) => {
              const isActive = idx === activeIndex;
              return (
                <Box asChild key={idx}>
                  <button
                    type="button"
                    onClick={() => scrollTo(idx)}
                    aria-label={`Go to slide ${idx + 1}`}
                    aria-current={isActive ? "true" : undefined}
                    className={cn(
                      "flex h-2 w-10 rounded-full outline-none focus:outline-none",
                      isActive ? "bg-white" : "bg-white/20",
                    )}
                  />
                </Box>
              );
            })}
          </Flex>
        </Box>
      )}
    </Box>
  );
}
